import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import AuthModal from '@/components/AuthModal';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Menu, X, GraduationCap, ChevronLeft, ChevronRight, Star, MapPin, Clock, Users, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { trackClick } from '@/components/AnalyticsTracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [contactForm, setContactForm] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [showcaseCourses, setShowcaseCourses] = useState([]);
  const [courseStats, setCourseStats] = useState({ schools: 0, courses: 0, styles: 0 });
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/courses`).then(res => {
      const courses = (res.data || []).filter(c => c.status === 'confirmed' || c.status === 'active' || !c.status);
      // Shuffle randomly
      for (let i = courses.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [courses[i], courses[j]] = [courses[j], courses[i]];
      }
      setShowcaseCourses(courses);
      const styles = new Set(courses.map(c => c.martial_arts_style).filter(Boolean));
      setCourseStats({ schools: new Set(courses.map(c => c.school_id).filter(Boolean)).size, courses: courses.length, styles: styles.size || 6 });
    }).catch(() => {});
  }, []);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * 340, behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.firstName || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in your name, email and message');
      return;
    }
    setContactSubmitting(true);
    trackClick('contact_submit', 'Contact Form Submit');
    try {
      await axios.post(`${API}/contact`, {
        first_name: contactForm.firstName, last_name: contactForm.lastName,
        email: contactForm.email, phone: contactForm.phone, message: contactForm.message,
      });
      toast.success('Message sent! We will be in touch soon.');
      setContactForm({ firstName: '', lastName: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally { setContactSubmitting(false); }
  };

  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [branding, setBranding] = useState({ logo_url: null, site_name: 'Train In Japan' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => { fetchBranding(); }, []);
  const fetchBranding = async () => {
    try { const r = await axios.get(`${API}/branding/logo`); setBranding(r.data); } catch (e) {}
  };

  const getMartialArts = () => [
    { titleKey: 'martialArts.asayama.title', descriptionKey: 'martialArts.asayama.description', image: 'https://raw.githubusercontent.com/administration-TMJ/images/main/IMG_5703.JPG' },
    { titleKey: 'martialArts.kyudo.title', descriptionKey: 'martialArts.kyudo.description', image: 'https://raw.githubusercontent.com/administration-TMJ/images/main/468449155_10161447120048692_3053896351382417673_n%20-%20Copy.jpg' },
    { titleKey: 'martialArts.karate.title', descriptionKey: 'martialArts.karate.description', image: 'https://images.unsplash.com/photo-1514050566906-8d077bae7046?w=800&q=80' },
    { titleKey: 'martialArts.aikido.title', descriptionKey: 'martialArts.aikido.description', image: 'https://raw.githubusercontent.com/administration-TMJ/images/main/IMG_5693.JPG' },
    { titleKey: 'martialArts.iaido.title', descriptionKey: 'martialArts.iaido.description', image: 'https://raw.githubusercontent.com/administration-TMJ/images/main/IMG_5690.JPG' },
    { titleKey: 'martialArts.aikijujutsu.title', descriptionKey: 'martialArts.aikijujutsu.description', image: 'https://raw.githubusercontent.com/administration-TMJ/images/main/IMG_5703.JPG' },
  ];

  const getCulturalArts = () => [
    { titleKey: 'culturalArts.calligraphy', image: 'https://images.unsplash.com/photo-1486303954368-398fea0e72cd?q=80&w=2940' },
    { titleKey: 'culturalArts.ikebana', image: 'https://images.unsplash.com/photo-1564868405024-af2aa9752aac?q=80&w=2940' },
    { titleKey: 'culturalArts.sumie', image: 'https://images.unsplash.com/photo-1546638008-efbe0b62c730?q=80&w=2940' },
    { titleKey: 'culturalArts.swordSmithing', image: 'https://raw.githubusercontent.com/administration-TMJ/images/main/10620355_10152547227283692_1873362983066131198_o_10152547227283692.jpg' },
  ];

  const formatPrice = (p, cur) => {
    if (!p) return '';
    if (cur === 'JPY') return `¥${Number(p).toLocaleString()}`;
    return `${cur || ''} ${Number(p).toLocaleString()}`;
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 via-emerald-50 to-slate-100">
      {/* Floating Logo */}
      <div className="fixed top-2 sm:top-3 left-3 sm:left-4 md:left-6 z-[60] cursor-pointer" onClick={() => navigate('/')}>
        <div className="flex flex-col items-center" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' }}>
          {branding.logo_url && (
            <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-white border-2 sm:border-[3px] border-emerald-600 overflow-hidden">
              <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
            </div>
          )}
          <span className="mt-1 text-xs sm:text-sm md:text-base font-bold text-slate-800 bg-white/90 px-2 py-0.5 rounded-full backdrop-blur-sm" style={{ fontFamily: 'Playfair Display, serif' }}>
            {branding.site_name}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-2 flex items-center justify-between">
          <div className="w-8 sm:w-12"></div>
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <button onClick={() => navigate("/programs")} className="text-slate-700 hover:text-emerald-700 font-medium transition-colors" data-track="nav_browse">Browse Programs</button>
            <button onClick={() => navigate("/faq")} className="text-slate-700 hover:text-emerald-700 font-medium transition-colors">{t("nav.faq")}</button>
            <Button data-testid="nav-schools" onClick={() => navigate('/register-school')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-6 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />{t('nav.forSchools')}
            </Button>
            <div className="flex items-center gap-2">
              {user ? (
                <Button data-testid="nav-dashboard-btn" onClick={() => navigate('/dashboard')} size="sm" className="bg-emerald-700 hover:bg-emerald-800">{t('nav.dashboard')}</Button>
              ) : (
                <Button data-testid="nav-login-btn" onClick={() => setAuthModalOpen(true)} size="sm" className="bg-emerald-700 hover:bg-emerald-800">{t('nav.signIn')}</Button>
              )}
              <LanguageSwitcher />
            </div>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
            {mobileMenuOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 px-4 py-3 space-y-2">
            <button onClick={() => { navigate("/programs"); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-slate-700 font-medium">Browse Programs</button>
            <button onClick={() => { navigate("/faq"); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-slate-700 font-medium">{t("nav.faq")}</button>
            <Button onClick={() => { navigate('/register-school'); setMobileMenuOpen(false); }} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-2 rounded-lg shadow-md flex items-center justify-center gap-2">
              <GraduationCap className="w-5 h-5" />{t('nav.forSchools')}
            </Button>
            <div className="flex items-center gap-2 pt-1">
              {user ? (
                <Button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} size="sm" className="flex-1 bg-emerald-700">{t('nav.dashboard')}</Button>
              ) : (
                <Button onClick={() => { setAuthModalOpen(true); setMobileMenuOpen(false); }} size="sm" className="flex-1 bg-emerald-700">{t('nav.signIn')}</Button>
              )}
              <LanguageSwitcher />
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Enhanced with stats */}
      <section data-testid="hero-section" className="relative pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div data-testid="hero-content">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                {t('hero.title')}
              </h1>
              <p className="text-lg text-slate-600 mb-8" style={{ fontFamily: 'Spectral, serif' }}>
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Button data-testid="hero-explore-btn" onClick={() => { trackClick('hero_explore', 'Explore Programs'); navigate('/programs'); }} size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white px-8 text-base shadow-lg hover:shadow-xl transition-all">
                  {t('hero.explorePrograms')} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button data-testid="hero-contact-btn" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} size="lg" variant="outline" className="border-slate-300 hover:bg-slate-100 text-base">
                  {t('hero.contactUs')}
                </Button>
              </div>
              {/* Trust indicators */}
              <div className="flex flex-wrap gap-6 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-emerald-600" /> {courseStats.schools || 'Multiple'} {courseStats.schools === 1 ? 'School' : 'Schools'}</span>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-500" /> {courseStats.styles}+ Martial Arts</span>
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-red-500" /> {courseStats.courses || 'Multiple'} {courseStats.courses === 1 ? 'Program' : 'Programs'}</span>
              </div>
            </div>
            <div className="relative">
              <img src="https://raw.githubusercontent.com/administration-TMJ/images/main/IMG_6095.JPG" alt="Traditional Japanese dojo" className="rounded-2xl shadow-2xl w-full h-[400px] sm:h-[500px] object-cover" />
              {branding.logo_url && (
                <div className="absolute top-6 left-6 w-14 h-14 md:w-20 md:h-20 rounded-full bg-white border-4 border-white shadow-xl overflow-hidden">
                  <img src={branding.logo_url} alt={branding.site_name} className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* === COURSE SHOWCASE — AUTO-SCROLLING MARQUEE === */}
      {showcaseCourses.length > 0 && (
        <section data-testid="course-showcase" className="py-12 sm:py-16 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Available Programs
            </h2>
            <p className="text-slate-500 mt-1">Book your authentic Japanese martial arts experience</p>
          </div>
          <style>{`
            @keyframes marqueeScroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .marquee-track {
              animation: marqueeScroll var(--marquee-duration, 40s) linear infinite;
            }
            .marquee-track:hover {
              animation-play-state: paused;
            }
          `}</style>
          <div className="relative">
            {/* Left fade */}
            <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
            {/* Right fade */}
            <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
            <div className="overflow-hidden">
              <div className="marquee-track flex gap-5 pb-4 w-max" style={{ '--marquee-duration': `${Math.max(showcaseCourses.length * 5, 30)}s` }}>
                {/* Duplicate the list for seamless loop */}
                {[...showcaseCourses, ...showcaseCourses].map((course, idx) => (
                  <div key={`${course.id || idx}-${idx}`} data-testid={idx < showcaseCourses.length ? `showcase-course-${idx}` : undefined} className="flex-shrink-0 w-[280px] sm:w-[310px] group cursor-pointer" onClick={() => { trackClick('course_click', course.title || 'Course'); navigate(`/programs/${course.id}`); }}>
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                      <div className="relative h-44 overflow-hidden">
                        <img src={course.image_url || 'https://images.unsplash.com/photo-1514050566906-8d077bae7046?w=600&q=80'} alt={course.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        {course.martial_arts_style && (
                          <span className="absolute top-3 left-3 bg-emerald-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                            {course.martial_arts_style}
                          </span>
                        )}
                        {course.price && (
                          <span className="absolute top-3 right-3 bg-white/95 text-slate-900 text-sm font-bold px-3 py-1 rounded-full shadow-md backdrop-blur-sm">
                            {formatPrice(course.price, course.currency)}
                          </span>
                        )}
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <h3 className="font-semibold text-slate-900 text-base mb-1 line-clamp-2" style={{ fontFamily: 'Spectral, serif' }}>{course.title || 'Martial Arts Course'}</h3>
                        <p className="text-slate-500 text-sm mb-3 line-clamp-2">{course.description?.substring(0, 100) || 'Experience authentic training in Japan'}</p>
                        <div className="mt-auto flex items-center justify-between text-xs text-slate-400">
                          {course.experience_level && <span className="capitalize bg-slate-100 px-2 py-0.5 rounded">{course.experience_level}</span>}
                          {course.class_type && <span className="capitalize">{course.class_type}</span>}
                        </div>
                      </div>
                      <div className="px-4 pb-4">
                        <button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5 rounded-lg transition-colors text-sm" data-track="book_click">
                          View & Book
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-6 text-center">
            <button onClick={() => { trackClick('browse_all_click', 'Browse All Programs'); navigate('/programs'); }} className="text-emerald-700 hover:text-emerald-800 font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all">
              Browse All {showcaseCourses.length} Programs <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {/* Discover Section */}
      <section data-testid="discover-section" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t('sections.discoverTheArt')}</h2>
          <p className="text-center text-slate-600 max-w-3xl mx-auto mb-12">{t('sections.discoverIntro')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { titleKey: 'sections.authenticTraining', descKey: 'sections.authenticTrainingDesc', icon: '🥋' },
              { titleKey: 'sections.culturalImmersion', descKey: 'sections.culturalImmersionDesc', icon: '🏯' },
              { titleKey: 'sections.allLevelsWelcome', descKey: 'sections.allLevelsWelcomeDesc', icon: '🎯' },
            ].map((item, idx) => (
              <Card key={idx} className="border-slate-200 hover:shadow-lg transition-all hover:-translate-y-1 duration-300">
                <CardHeader>
                  <div className="text-3xl mb-2">{item.icon}</div>
                  <CardTitle style={{ fontFamily: 'Spectral, serif' }}>{t(item.titleKey)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{t(item.descKey)}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Martial Arts Section */}
      <section data-testid="martial-arts-section" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t('sections.availableForBookings')}</h2>
          <p className="text-center text-slate-600 max-w-3xl mx-auto mb-12">{t('sections.availableForBookingsDesc')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {getMartialArts().map((art, idx) => (
              <div key={idx} data-testid={`martial-art-${idx}`} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer" onClick={() => { trackClick('martial_art_click', t(art.titleKey)); navigate('/programs'); }}>
                <img src={art.image} alt={t(art.titleKey)} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent flex flex-col justify-end p-6">
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Spectral, serif' }}>{t(art.titleKey)}</h3>
                  <p className="text-slate-200 text-sm">{t(art.descriptionKey)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button onClick={() => { trackClick('browse_more', 'AND MORE'); navigate('/programs'); }} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold px-8">
              ...AND MORE <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Cultural Arts Section */}
      <section data-testid="cultural-arts-section" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-900 mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>{t('sections.culturalArtsTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {getCulturalArts().map((art, idx) => (
              <div key={idx} data-testid={`cultural-art-${idx}`} className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow group cursor-pointer" onClick={() => navigate('/programs')}>
                <img src={art.image} alt={t(art.titleKey)} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 to-transparent flex items-end p-4">
                  <h3 className="text-white font-semibold">{t(art.titleKey)}</h3>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button onClick={() => navigate('/programs')} variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold px-8">
              ...AND MORE <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-r from-emerald-700 to-emerald-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t('cta.title')}</h2>
          <p className="text-emerald-100 text-lg mb-8">{t('cta.subtitle')}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button data-testid="cta-browse-btn" onClick={() => { trackClick('cta_browse', 'Browse Programs CTA'); navigate('/programs'); }} size="lg" className="bg-white text-emerald-800 hover:bg-emerald-50 font-semibold px-8 shadow-lg">
              {t('cta.button')} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button data-testid="cta-contact-btn" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} size="lg" variant="outline" className="border-white/50 text-white hover:bg-white/10 font-semibold px-8">
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" data-testid="contact-section" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-slate-900 mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>{t('contact.title')}</h2>
          <Card>
            <CardContent className="p-8">
              <form className="space-y-6" onSubmit={handleContactSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('contact.firstName')} *</label>
                    <input data-testid="contact-firstname" type="text" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" value={contactForm.firstName} onChange={e => setContactForm({...contactForm, firstName: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">{t('contact.lastName')}</label>
                    <input data-testid="contact-lastname" type="text" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" value={contactForm.lastName} onChange={e => setContactForm({...contactForm, lastName: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('contact.email')} *</label>
                  <input data-testid="contact-email" type="email" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('contact.phone')}</label>
                  <input data-testid="contact-phone" type="tel" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">{t('contact.message')} *</label>
                  <textarea data-testid="contact-message" rows={4} className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})}></textarea>
                </div>
                <Button data-testid="contact-submit-btn" type="submit" disabled={contactSubmitting} className="w-full bg-emerald-700 hover:bg-emerald-800">{contactSubmitting ? 'Sending...' : t('contact.submit')}</Button>
              </form>
              <div className="mt-8 text-center">
                <p className="text-slate-600">{t('contact.emailLabel')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t('footer.title')}</h3>
          <p className="text-slate-400">{t('footer.subtitle')}</p>
          <div className="mt-6 flex justify-center gap-6">
            <button onClick={() => navigate('/programs')} className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors">Browse Programs</button>
            <button onClick={() => navigate('/faq')} className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors">FAQ</button>
          </div>
          <p className="text-slate-500 mt-8 text-sm">{t('footer.copyright')}</p>
        </div>
      </footer>

      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </div>
  );
};

export default LandingPage;
